import {CAMPAIGN_KEYS} from "@/api/campaignApi";
import RightChevronIcon from "@/assets/colorable/right_chevron.svg?react";
import FullPageLoader from "@/components/Common/FullPageLoader";
import PermissionDenied from "@/components/Common/PermissionDenied";
import type {CampaignOwnerDetailSerializer, TokenClaimsSerializer} from "@/generated/DjangoTypes";
import usePackageLimits from "@/hooks/usePackageLimits";
import {useGenericQuery} from "@/hooks/useQueryHooks";
import CampaignCreationErrors from "@/pages/campaignForm/CampaignCreationErrors";
import CampaignFormTypeSelect from "@/pages/campaignForm/CampaignFormTypeSelect";
import CampaignNavbar from "@/pages/campaignForm/CampaignNavbar";
import CampaignPreview from "@/pages/campaignForm/CampaignPreview";
import {PreviewProvider} from "@/pages/campaignForm/CampaignPreviewContext";
import DuplicateButton from "@/pages/campaignForm/steps/DuplicateButton";
import CampaignFormStepOne from "@/pages/campaignForm/steps/stepOne/CampaignFormStepOne";
import CampaignFormStepOneSocials from "@/pages/campaignForm/steps/stepOne/CampaignFormStepOne.socials";
import CampaignFormStepThree from "@/pages/campaignForm/steps/stepThree/CampaignFormStepThree";
import CampaignFormStepTwoSocials from "@/pages/campaignForm/steps/stepTwo/CampaignFormStepTwo.socials";
import CampaignFormStepTwoUgc from "@/pages/campaignForm/steps/stepTwo/CampaignFormStepTwo.ugc";
import useTiktokCampaignKeywordSeeder from "@/pages/campaignForm/useTiktokCampaignKeywordSeeder";
import useTiktokKeywordLimitValidation from "@/pages/campaignForm/useTiktokKeywordLimitValidation";
import {AbilityContext} from "@/permissions";
import {capturePostHogEvent, PostHogEvents} from "@/posthog";
import {
  useCampaignFormActions,
  useCampaignFormStore,
  useComposedCampaignState,
  useComposedTiktokCampaignState,
  useMiscActions,
  useSetCampaignFormState,
} from "@/store/campaignForm";
import useCampaignManagementStore from "@/store/campaignManagement";
import {useGroupStore} from "@/store/group";
import {
  campaignFieldsAtom,
  hasStartedCampaignCreationAtom,
  selectedCompanyIdAtom,
  showPartnershipAdsTutorialCtaAtom,
  showSparkAdsTutorialCtaAtom,
} from "@/store/jotaiAtoms";
import useTokenStore from "@/store/token";
import {fetchCampaign} from "@/utils/loaderUtils";
import {createFileRoute, redirect, useRouter} from "@tanstack/react-router";
import {zodValidator} from "@tanstack/zod-adapter";
import dayjs from "dayjs";
import {getDefaultStore, useSetAtom} from "jotai";
import {jwtDecode} from "jwt-decode";
import {debounce} from "lodash";
import {useContext, useEffect} from "react";
import {useTranslation} from "react-i18next";
import {useEffectOnce, useTitle} from "react-use";
import {z} from "zod";

const AI_DRAFT_REVIEW_FIELDS = [
  "brandDoes",
  "cashReward",
  "extraCashReward",
  "instagram-deliverables",
  "sellingPrice",
  "task",
  "tiktok-deliverables",
];

const AI_DRAFT_UNEDITED_TOLERANCE_SECONDS = 5;

const debouncedSaveCampaign = debounce(
  () => useCampaignFormStore.getState().actions.misc.saveCampaign().catch(console.log),
  2000,
);
let tempDebouncedSaveCampaign = debouncedSaveCampaign;

export const Route = createFileRoute("/_authenticated/campaign/$campaignId/edit")({
  component: CampaignEdit,
  params: {
    parse: raw => ({campaignId: +raw.campaignId}),
    stringify: params => ({campaignId: params.campaignId?.toString()}),
  },
  validateSearch: zodValidator(
    z.object({
      isUgc: z.boolean().optional(),
      step: z.number().optional(),
    }),
  ),
  loader: async ({params}) => {
    try {
      const token = useTokenStore.getState().access;
      const campaign = await fetchCampaign(params.campaignId);

      if (!token) {
        return {campaign};
      }

      const claims = jwtDecode<TokenClaimsSerializer>(token);
      const companyId = campaign.company_profile?.id;
      const hasAccessToSubBrand = claims.sub_brand_ids?.includes(companyId!);
      const isAgency = claims.access_level === "AGENCY";

      if (isAgency && hasAccessToSubBrand && !!companyId) {
        getDefaultStore().set(selectedCompanyIdAtom, companyId);
      }

      return;
    } catch (error) {
      console.log("Error loading campaign:", error);
      throw redirect({to: "/login", replace: true});
    }
  },
});

function CampaignEdit() {
  const {t} = useTranslation(["common", "campaign_overview"]);
  const navigate = Route.useNavigate();
  const {step} = Route.useSearch();
  const router = useRouter();
  const abilityContext = useContext(AbilityContext);
  const campaignId = Route.useParams().campaignId;
  const campaignMisc = useComposedCampaignState(campaign => campaign.misc);
  const campaignState = useComposedCampaignState();
  const campaignType = useComposedCampaignState(state => state.misc.campaignType);
  const hasTiktokKeywords = useComposedTiktokCampaignState(
    state => state.keywordPlan.existing_keyword_ids.length > 0 || state.keywordPlan.new_keywords.length > 0,
  );
  const clearKeywordPlan = useCampaignFormActions(state => state.tiktok.clearKeywordPlan);
  const initWithExistingCampaign = useCampaignFormActions(state => state.misc.initWithExistingCampaign);
  const isDraft = useComposedCampaignState(state => state.misc.status === "draft");
  const isNew = !campaignId;
  const resetCampaignForm = useMiscActions().resetStore;
  const resetGroupStore = useGroupStore(state => state.reset);
  const setCampaignFields = useSetAtom(campaignFieldsAtom);
  const setFormState = useSetCampaignFormState();
  const setCampaignManagementValue = useCampaignManagementStore(state => state.setValue);
  const setShowPartnershipAdsTutorialCta = useSetAtom(showPartnershipAdsTutorialCtaAtom);
  const setShowSparkAdsTutorialCta = useSetAtom(showSparkAdsTutorialCtaAtom);
  const setStartedCampaignCreation = useSetAtom(hasStartedCampaignCreationAtom);
  const {packageCategory} = usePackageLimits({isEnabled: !!campaignId});
  useTiktokCampaignKeywordSeeder();
  useTiktokKeywordLimitValidation();
  useTitle(t("campaign_overview:edit_campaign"));

  const canEditCampaign = abilityContext.can("update", "Campaign");

  const campaignEditQuery = useGenericQuery<CampaignOwnerDetailSerializer>({
    queryKey: CAMPAIGN_KEYS.qCampaignEdit(Number(campaignId)),
    enabled: canEditCampaign && !!campaignId,
    refetchOnMount: true,
  });

  useEffectOnce(() => {
    setStartedCampaignCreation(true);
    setShowPartnershipAdsTutorialCta(true);
    setShowSparkAdsTutorialCta(true);
  });
  useEffect(() => {
    tempDebouncedSaveCampaign.cancel();
    tempDebouncedSaveCampaign = debouncedSaveCampaign;

    if (isDraft && !!campaignState.misc.id) {
      tempDebouncedSaveCampaign();
    }
  }, [isDraft, debouncedSaveCampaign, campaignState]);
  useEffect(() => {
    if (!campaignEditQuery.data || isNew) return;
    initWithExistingCampaign(campaignEditQuery.data);
    setCampaignManagementValue("campaign", campaignEditQuery.data);
    setCampaignFields({
      brand_does: {value: "", history: [], currentHistoryIndex: -1},
      campaign_description: {value: "", history: [], currentHistoryIndex: -1},
      task_description: {value: "", history: [], currentHistoryIndex: -1},
    });

    const {ai_creation_prompt, created, last_edited, service_is_voucher, status} = campaignEditQuery.data;
    const isUneditedAiDraft =
      status === "draft" &&
      !!ai_creation_prompt &&
      !!created &&
      !!last_edited &&
      Math.abs(dayjs(last_edited).diff(created, "second")) <= AI_DRAFT_UNEDITED_TOLERANCE_SECONDS;

    if (isUneditedAiDraft) {
      setFormState(
        "warnings",
        service_is_voucher ? [...AI_DRAFT_REVIEW_FIELDS, "digitalShopUrl"] : AI_DRAFT_REVIEW_FIELDS,
      );
    }
  }, [campaignEditQuery.data, initWithExistingCampaign, isNew]);
  useEffect(() => {
    return () => {
      resetCampaignForm();
      resetGroupStore();
      setShowPartnershipAdsTutorialCta(true);
      setShowSparkAdsTutorialCta(true);
    };
  }, [resetCampaignForm, resetGroupStore, setShowPartnershipAdsTutorialCta, setShowSparkAdsTutorialCta]);
  useEffect(() => {
    if (!step) {
      navigate({search: prev => ({...prev, step: 1}), replace: true});
    }
  }, [navigate, step]);

  if ((!campaignEditQuery.data || !campaignMisc.id) && !isNew) {
    return <FullPageLoader />;
  }

  if (!canEditCampaign || campaignEditQuery.error?.response?.status === 403) {
    return <PermissionDenied />;
  }

  return (
    <>
      <meta name="robots" content="noindex" />
      <div className="max-w-wide relative mx-auto flex gap-10 px-8 pb-8">
        <div className="relative z-20 w-full">
          <div className="sticky top-16 z-40 -mx-8 bg-white px-8 py-5">
            <div className="flex w-full flex-col gap-2 sm:gap-5 md:flex-row">
              <button
                onClick={() => {
                  capturePostHogEvent(PostHogEvents.CampaignCreationCancelled, {
                    campaignId: Number(campaignId),
                    companyId: campaignState.misc.companyId ? Number(campaignState.misc.companyId) : undefined,
                    campaignType,
                  });

                  if (step === 1) {
                    router.history.back();
                  } else {
                    navigate({
                      to: "/campaign/$campaignId/edit",
                      params: {campaignId},
                      search: {isUgc: campaignType === "ugc", step: step === 2 ? 1 : 2},
                      replace: true,
                    });
                  }
                }}
                className="text-neutral-black hover:text-neutral-grey-dark border-neutral-grey-light-dark flex h-10 w-fit items-center gap-2 rounded-sm border px-4 text-sm font-medium">
                <RightChevronIcon className="h-3 shrink-0 rotate-180" />
                {t("modalBack")}
              </button>
              {isDraft && !!packageCategory && (
                <CampaignFormTypeSelect
                  hasKeywords={hasTiktokKeywords}
                  onClearKeywords={clearKeywordPlan}
                  selected={campaignType}
                />
              )}
              {(campaignMisc.status === "draft" || campaignMisc.status === "planned") && (
                <div className="md:ml-auto xl:pr-5">
                  <DuplicateButton />
                </div>
              )}
            </div>
          </div>
          <PreviewProvider>
            <div className="flex gap-10">
              <div className="flex w-full flex-col gap-5 xl:flex-row xl:gap-10">
                <div className="relative">
                  <div className="scrollbar-hide sticky top-36 z-30 overflow-y-scroll bg-white pt-5 xl:max-h-[calc(100vh-144px)] xl:pb-35">
                    <CampaignNavbar />
                  </div>
                  <div className="pointer-events-none absolute top-0 left-0 z-30 h-5 w-full bg-linear-to-b from-white to-transparent max-xl:hidden" />
                </div>
                <div className="flex w-full flex-col">
                  {step === 1 && (campaignType === "ugc" ? <CampaignFormStepOne /> : <CampaignFormStepOneSocials />)}
                  {step === 2 && (campaignType === "ugc" ? <CampaignFormStepTwoUgc /> : <CampaignFormStepTwoSocials />)}
                  {step === 3 && <CampaignFormStepThree />}
                  <CampaignCreationErrors />
                </div>
              </div>
              <div className="relative max-md:hidden">
                <CampaignPreview />
                <div className="pointer-events-none absolute -top-1 left-0 z-30 h-5 w-full bg-linear-to-b from-white to-transparent" />
              </div>
            </div>
          </PreviewProvider>
        </div>
      </div>
    </>
  );
}
